import React, { useEffect, useState } from 'react';
import { fetchCatalogue } from './services/catalogueService';
import { BranchColumn } from './components/MillerColumn';
import useMillerTree, { isAccessoriesArray } from './useMillerTree';
import { Box, Typography, Button, Tooltip } from '@mui/material';
import DeviceDrawer from './components/DeviceDrawer';
import CatalogueViewer from './components/CatalogueViewer';

function extractPath(d) {
  const out = [];
  for (let i = 1; i <= 10; i++) {
    const v = d[`cat_${i}`];
    if (v && !isAccessoriesArray(v)) out.push(v);
    else break;
  }
  return out;
}

/**
 * Device Admin — the catalogue/pricing Miller-column editor.
 * (Auth and the top app bar are handled by AuthGate + Layout.)
 */
export default function DeviceEditor() {
  const [devices, setDevices] = useState([]);
  const [drawer, setDrawer] = useState({ open: false, edit: null, path: [] });
  const [viewMode, setViewMode] = useState('miller'); // or 'table'

  function drawerPathMatch(device, fullPath) {
    return fullPath.every((v, i) => device[`cat_${i + 1}`] === v);
  }

  /* quick test: is there already a device at `p` (no deeper cats) */
  function deviceExistsAt(p = []) {
    return devices.some((d) => {
      const nextCat = d[`cat_${p.length + 1}`];
      return (
        p.every((v, i) => d[`cat_${i + 1}`] === v) &&
        (!nextCat || isAccessoriesArray(nextCat)) &&
        (d.Type || d.type)
      );
    });
  }

  function addDevice(label, level) {
    const devicePath = [...path.slice(0, level), label];

    if (deviceExistsAt(devicePath)) {
      const dev = devices.find((d) => devicePath.every((v, i) => d[`cat_${i + 1}`] === v));
      openDrawerForEdit(dev.UID);
      return;
    }

    /* reflect the new selection so the pill appears once saved */
    select(level, label);

    setDrawer({ open: true, edit: null, path: devicePath });
  }

  function openDrawerForEdit(uid) {
    const dev = devices.find((d) => d.UID === uid);
    setDrawer({ open: true, edit: dev, path: extractPath(dev) });
  }

  // load once
  useEffect(() => {
    fetchCatalogue().then(setDevices);
  }, []);

  const { columns, path, select, branchStatus, isLeaf, addBranch, clearBranches } =
    useMillerTree(devices);

  function resetLocalBranches() {
    if (typeof clearBranches === 'function') clearBranches();
  }

  return (
    <Box>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backgroundColor: 'background.paper',
          borderBottom: '1px solid #ddd',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          p: 2,
        }}
      >
        <Typography variant="h6">
          Device Editor ({viewMode === 'miller' ? 'Column View' : 'Grid View'})
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tooltip
            title="Grid View is temporarily disabled pending performance upgrades and schema updates (Accessories Array & Type casing)."
            arrow
          >
            <span>
              <Button variant="outlined" size="small" disabled>
                Switch to Grid View
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {viewMode === 'table' ? (
        <CatalogueViewer
          rows={devices}
          setRows={(newDevices) => {
            setDevices(newDevices);
            resetLocalBranches();
          }}
        />
      ) : (
        <Box sx={{ display: 'flex', p: 2 }}>
          {columns.map((values, lvl) => (
            <BranchColumn
              key={lvl}
              items={values.map((v) => {
                const fullPath = [...path.slice(0, lvl), v];
                const isDevice = devices.some((d) => {
                  const nextCat = d[`cat_${lvl + 2}`];
                  return (
                    fullPath.every((val, idx) => d[`cat_${idx + 1}`] === val) &&
                    (!nextCat || isAccessoriesArray(nextCat)) &&
                    (d.Type || d.type)
                  );
                });
                return {
                  key: v,
                  label: v,
                  status: branchStatus(lvl, v),
                  isLeaf: isLeaf(lvl, v),
                  isDevice,
                };
              })}
              selected={path[lvl]}
              allowDevice={!deviceExistsAt(path.slice(0, lvl))}
              onSelect={(item) => {
                select(lvl, item.label);
                if (item.isLeaf) {
                  const match = devices.find((d) =>
                    drawerPathMatch(d, [...path.slice(0, lvl), item.label])
                  );
                  if (match) openDrawerForEdit(match.UID);
                }
              }}
              onAddBranch={(label) => addBranch(label, lvl)}
              onAddDevice={(label) => addDevice(label, lvl)}
            />
          ))}
          <DeviceDrawer
            open={drawer.open}
            onClose={() => {
              setDrawer({ open: false, edit: null, path: [] });
              fetchCatalogue().then(setDevices);
            }}
            initial={drawer.edit}
            path={drawer.path}
          />
        </Box>
      )}
    </Box>
  );
}
